/**
 * Validation and persistence normalization for Clinical Trials tenant settings.
 */

import type {
  ClinicalTrialsFacilitySettings,
  HealthCareFacilityModel,
} from "../../../data-model/healthFacility";

/** Empty nested settings used when a facility first selects Clinical Trials. */
export const EMPTY_CLINICAL_TRIALS_SETTINGS: ClinicalTrialsFacilitySettings = {
  site_id: "",
  fhir: { org_code: "" },
  ingest: { credential_id: "" },
  email: {
    recipients: [],
    from_email: "",
    subject_prefix: "",
  },
  dashboard: { url: "" },
};

/**
 * Returns whether every backend-required Clinical Trials tenant setting is present.
 *
 * @param settings - Nested facility tenant settings to validate
 * @returns True when every string and at least one recipient are non-empty
 */
export function areClinicalTrialsSettingsComplete(
  settings: ClinicalTrialsFacilitySettings | undefined,
): boolean {
  if (!settings) return false;
  return Boolean(
    settings.site_id.trim() &&
    settings.fhir.org_code.trim() &&
    settings.ingest.credential_id.trim() &&
    settings.email.recipients.some((recipient) => recipient.trim()) &&
    settings.email.from_email.trim() &&
    settings.email.subject_prefix.trim() &&
    settings.dashboard.url.trim(),
  );
}

/**
 * Trims Clinical Trials settings and removes empty email recipients before persistence.
 *
 * @param settings - Nested tenant settings collected by the facility form
 * @returns Exact normalized schema consumed by backend TenantContext
 */
export function normalizeClinicalTrialsSettings(
  settings: ClinicalTrialsFacilitySettings,
): ClinicalTrialsFacilitySettings {
  return {
    site_id: settings.site_id.trim(),
    fhir: { org_code: settings.fhir.org_code.trim() },
    ingest: { credential_id: settings.ingest.credential_id.trim() },
    email: {
      recipients: settings.email.recipients
        .map((recipient) => recipient.trim())
        .filter(Boolean),
      from_email: settings.email.from_email.trim(),
      subject_prefix: settings.email.subject_prefix.trim(),
    },
    dashboard: { url: settings.dashboard.url.trim() },
  };
}

/**
 * Produces facility form data with the exact nested Clinical Trials schema when required.
 *
 * @param facilityData - Current facility form state
 * @returns Facility data with compatible display-name aliases and normalized CT settings
 * @throws Error when a Clinical Trials facility is missing required settings
 */
export function prepareFacilityDataForPersistence(
  facilityData: HealthCareFacilityModel,
): HealthCareFacilityModel {
  if (facilityData.dashboard_type !== "clinical_trials") return facilityData;
  const settings = facilityData.clinical_trials;
  if (!settings || !areClinicalTrialsSettingsComplete(settings)) {
    throw new Error("Clinical Trials tenant settings are incomplete");
  }
  const displayName = (facilityData.Name || facilityData.name || "").trim();
  return {
    ...facilityData,
    Name: displayName,
    name: displayName,
    is_external_organization: true,
    is_active: true,
    clinical_trials: normalizeClinicalTrialsSettings(settings),
  };
}
